import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { ROUTES, API_BASE_URL, API_ENDPOINTS } from '@utils/constants';
import Layout from '@components/layout/Layout';

type CallbackStatus = 'processing' | 'success' | 'error';

const AuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithTokens, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const processedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Check for error first
      const error = searchParams.get('error');
      if (error) {
        setStatus('error');
        setErrorMessage(decodeURIComponent(error));
        return;
      }

      // Get authorization code from URL (not tokens)
      const code = searchParams.get('code');

      if (!code) {
        setStatus('error');
        setErrorMessage('Missing authorization code');
        return;
      }

      if (processedCodeRef.current === code) {
        return;
      }

      processedCodeRef.current = code;
      window.history.replaceState({}, document.title, window.location.pathname);

      try {
        // Exchange the authorization code for tokens via POST
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH_OAUTH_EXCHANGE}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to exchange authorization code');
        }

        const tokens = await response.json();

        // Authenticate with the returned user profile to avoid a follow-up
        // /auth/me call while completing the OAuth callback.
        await loginWithTokens(tokens.access_token, tokens.refresh_token, tokens.user);
        setStatus('success');

        // Redirect to home after a brief success message
        setTimeout(() => {
          navigate(ROUTES.HOME, { replace: true });
        }, 1500);
      } catch (err: any) {
        setStatus('error');
        setErrorMessage(err.message || 'Authentication failed');
      }
    };

    // Only process if not already authenticated
    if (!isAuthenticated) {
      handleCallback();
    } else {
      // Already authenticated, redirect to home
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [searchParams, loginWithTokens, navigate, isAuthenticated]);

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {status === 'processing' && (
              <div className="text-center">
                <Loader2 className="mx-auto h-12 w-12 text-primary-600 animate-spin" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Completing sign in...
                </h2>
                <p className="mt-2 text-gray-600">
                  Please wait while we verify your authentication.
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center">
                <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Welcome back!
                </h2>
                <p className="mt-2 text-gray-600">
                  You have been successfully signed in. Redirecting...
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <XCircle className="mx-auto h-12 w-12 text-red-500" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Authentication Failed
                </h2>
                <p className="mt-2 text-gray-600">
                  {errorMessage || 'An error occurred during authentication.'}
                </p>
                <button
                  onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
                  className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Return to Login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AuthCallbackPage;
